import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Advertisement } from '../types';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';
import { getPartnerLogoUrl } from '../utils/pocketbaseHelpers';
import { getTranslations, getLocaleFromRegion } from '../utils/translations';
import { formatDescriptionHtml } from '../utils/formatDescription';
import { ArrowLeft, Mail } from 'lucide-react';

export const DirectAdView: React.FC = () => {
  const { adId } = useParams<{ adId: string }>();
  const navigate = useNavigate();
  const { advertisements, partners } = useAuth();
  const [ad, setAd] = useState<Advertisement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [email, setEmail] = useState('');
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [generatedPromocode, setGeneratedPromocode] = useState<string | null>(null);
  const [isDirectContactMessage, setIsDirectContactMessage] = useState(false);
  const [showPromocodeModal, setShowPromocodeModal] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [promoTargetUrl, setPromoTargetUrl] = useState<string | null>(null);
  const [promoEmailReminder, setPromoEmailReminder] = useState<string | null>(null);
  const [promoDeliveryNotice, setPromoDeliveryNotice] = useState<{
    title?: string;
    message: string;
    type: 'success' | 'info' | 'error';
  } | null>(null);
  const [showNoPromoConfirm, setShowNoPromoConfirm] = useState(false);
  const [showRedirectConfirm, setShowRedirectConfirm] = useState(false);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState<string | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    const loadAd = async () => {
      if (!adId) {
        setError('Advertisement ID is required');
        setLoading(false);
        return;
      }

      try {
        // Find ad in context first
        let advertisement = advertisements.find(a => a.id === adId);
        
        // If not in context, fetch directly
        if (!advertisement) {
          const { data, error: fetchError } = await DataService.getAdvertisement(adId);
          if (fetchError || !data) {
            setError('Advertisement not found');
            setLoading(false);
            return;
          }
          advertisement = data;
        }

        // Check if ad is accessible via direct link
        const mode = advertisement.advertisement_mode || 'default';
        if (mode === 'default') {
          setError('This advertisement is only available through campaigns');
          setLoading(false);
          return;
        }

        // Check if ad is approved
        if (advertisement.approval_status !== 'approved') {
          setError('This advertisement is not yet available');
          setLoading(false);
          return;
        }

        setAd(advertisement);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load advertisement:', err);
        setError('Failed to load advertisement');
        setLoading(false);
      }
    };

    loadAd();
  }, [adId, advertisements]);

  const partner = ad ? partners.find(p => p.id === ad.partner_id) : null;
  const locale = ad ? getLocaleFromRegion(ad.region) : 'en';
  const t = getTranslations(locale);

  // Determine provider info - use ad's provider info if set, otherwise fall back to partner
  const hasCustomProvider = ad && (ad.provider_entity_id || ad.provider_name);
  const providerName = hasCustomProvider && ad.provider_name ? ad.provider_name : partner?.name;
  const providerLogoUrl = hasCustomProvider && ad.provider_logo_url ? ad.provider_logo_url : (partner ? getPartnerLogoUrl(partner) : null);
  const providerPrivacyPolicyUrl = hasCustomProvider && ad.provider_privacy_policy_url ? ad.provider_privacy_policy_url : partner?.privacy_policy_url;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedToClipboard(true);
    setTimeout(() => setCopiedToClipboard(false), 2000);
  };

  const closePromocodeModal = () => {
    setShowPromocodeModal(false);
    setGeneratedPromocode(null);
    setCopiedToClipboard(false);
    setIsDirectContactMessage(false);
    setShowPromoForm(false);
    setPromoEmailReminder(null);
    setPromoTargetUrl(null);
  };

  const handleCancelNoPromo = () => {
    setShowNoPromoConfirm(false);
    setShowPromoForm(false);
  };

  const handleConfirmNoPromo = async () => {
    setShowNoPromoConfirm(false);
    await handlePromoRequest({ skipNoPromoConfirmation: true });
  };

  const handleCancelRedirect = () => {
    setShowRedirectConfirm(false);
    setPendingRedirectUrl(null);
    setShowPromoForm(false);
  };

  const handleConfirmRedirect = () => {
    if (pendingRedirectUrl) {
      window.location.href = pendingRedirectUrl;
    }
    setShowRedirectConfirm(false);
    setPendingRedirectUrl(null);
  };

  const handlePromoRequest = async (options?: { skipNoPromoConfirmation?: boolean; skipRedirectConfirmation?: boolean }) => {
    if (!ad) return;

    // Validation
    if (!email) {
      setPromoDeliveryNotice({
        title: t.missingEmailTitle,
        message: t.missingEmailMessage,
        type: 'error'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setPromoDeliveryNotice({
        title: t.invalidEmailTitle,
        message: t.invalidEmailMessage,
        type: 'error'
      });
      setEmail('');
      return;
    }

    if (!agreedToPrivacy) {
      setPromoDeliveryNotice({
        title: t.privacyConsentReminderTitle,
        message: t.privacyConsentReminderMessage,
        type: 'error'
      });
      return;
    }

    // Check if ad has promocode
    const hasPromocode = ad.has_promocode;

    // If no promocode, show confirmation modal
    if (!hasPromocode && !options?.skipNoPromoConfirmation) {
      setShowNoPromoConfirm(true);
      return;
    }

    setIsRequesting(true);

    try {
      const { data: promoResponse, error: promoError } = await DataService.requestPromocode(
        ad.id,
        email,
        agreedToPrivacy
      );

      if (promoError) {
        setPromoDeliveryNotice({
          title: 'Error',
          message: promoError,
          type: 'error'
        });
        setIsRequesting(false);
        return;
      }

      // Handle redirect response
      if (promoResponse?.redirect_url) {
        if (!options?.skipRedirectConfirmation) {
          setPendingRedirectUrl(promoResponse.redirect_url);
          setShowRedirectConfirm(true);
          setIsRequesting(false);
          return;
        }
        window.location.href = promoResponse.redirect_url;
        return;
      }

      const promocode = promoResponse?.promo_code;
      const deliveryMethods = promoResponse?.delivery_methods;
      const emailDelivery = promoResponse?.email_delivery;
      const shouldDisplayOnWeb = deliveryMethods?.web_display !== false;
      const targetUrl = ad.target_url_enabled ? ad.target_url : null;
      const messageText = promoResponse?.message || '';

      setShowPromoForm(false);

      const emailAndWeb = shouldDisplayOnWeb && deliveryMethods?.email;

      if (!shouldDisplayOnWeb && deliveryMethods?.email) {
        setGeneratedPromocode(null);
        setIsDirectContactMessage(false);
        setShowPromocodeModal(false);
        setPromoDeliveryNotice({
          title: emailDelivery?.sent ? t.promoSentEmailTitle : undefined,
          message: messageText || (emailDelivery?.sent
            ? t.promoSentEmailDescription.replace('{email}', email)
            : t.apiRequestSentDescription),
          type: emailDelivery?.sent ? 'success' : 'info'
        });
        setPromoEmailReminder(null);
      } else if (promocode) {
        setPromoDeliveryNotice(null);
        setGeneratedPromocode(promocode);
        setIsDirectContactMessage(messageText.toLowerCase().includes('partner will contact you'));
        setShowPromocodeModal(true);
        setPromoEmailReminder(emailAndWeb ? t.promoEmailReminderMessage : null);
        setPromoTargetUrl(targetUrl || null);
      } else {
        setPromoDeliveryNotice(messageText ? { message: messageText, type: 'info' } : null);
        setGeneratedPromocode(null);
        setShowPromocodeModal(false);
        setPromoEmailReminder(null);
      }
    } catch (error: any) {
      setPromoDeliveryNotice({
        title: 'Error',
        message: error.message || 'Failed to request promocode. Please try again.',
        type: 'error'
      });
    } finally {
      setIsRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading advertisement...</p>
        </div>
      </div>
    );
  }

  if (error || !ad) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Advertisement Not Available</h2>
          <p className="text-gray-600 mb-6">{error || 'This advertisement could not be found.'}</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <ArrowLeft size={20} />
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-6 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition"
        >
          <ArrowLeft size={16} />
          Back to Home
        </button>

        {/* Advertisement Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Provider Header - shows provider info (custom or partner) */}
          {providerName && (
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white">
              <div className="flex items-center gap-3">
                {providerLogoUrl && (
                  <img
                    src={providerLogoUrl}
                    alt={providerName}
                    className="w-12 h-12 rounded-full bg-white p-1.5 object-contain"
                  />
                )}
                <div>
                  <h1 className="text-xl font-bold">{providerName}</h1>
                  <p className="text-sm text-blue-100">Exclusive Offer</p>
                </div>
              </div>
            </div>
          )}

          {/* Advertisement Content */}
          <div className="p-5">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{ad.name}</h2>
            
            {/* Banner */}
            {ad.banner_url && (
              <div className="relative mb-4 rounded-lg overflow-hidden">
                <img
                  src={ad.banner_url}
                  alt={ad.name}
                  className="w-full h-auto"
                />
                {ad.badge_text && (
                  <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-white font-bold text-xs ${
                    ad.badge_color === 'red' ? 'bg-red-500' :
                    ad.badge_color === 'blue' ? 'bg-blue-500' :
                    ad.badge_color === 'green' ? 'bg-green-500' :
                    ad.badge_color === 'yellow' ? 'bg-yellow-400 text-gray-900' :
                    ad.badge_color === 'purple' ? 'bg-purple-500' :
                    'bg-orange-500'
                  }`}>
                    {ad.badge_text}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div 
              className="prose prose-sm max-w-none mb-5 text-gray-700"
              dangerouslySetInnerHTML={{ __html: formatDescriptionHtml(ad.description) }}
            />

            {/* Get Offer Button or Promo Form */}
            {showPromoForm ? (
              <div className="space-y-3">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t.enterYourEmail || "Enter your email"}
                    required
                  />
                </div>
                <div className="flex items-start space-x-2 py-1">
                  <div 
                    className="flex items-center cursor-pointer"
                    onClick={() => setAgreedToPrivacy(!agreedToPrivacy)}
                  >
                    <div className={`w-3.5 h-3.5 border-2 rounded flex items-center justify-center ${
                      agreedToPrivacy 
                        ? 'bg-blue-600 border-blue-600' 
                        : 'border-gray-300 bg-white'
                    }`}>
                      {agreedToPrivacy && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <label 
                    className="text-sm text-gray-600 cursor-pointer select-none flex-1"
                    onClick={() => setAgreedToPrivacy(!agreedToPrivacy)}
                  >
                    {t.agreeWithPrivacy}{' '}
                    <a
                      href={`/privacy-policy?locale=${locale}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t.platformPrivacyPolicy}
                    </a>
                    {providerPrivacyPolicyUrl && (
                      <>
                        {' '}{t.and}{' '}
                        <a 
                          href={providerPrivacyPolicyUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t.partnerPrivacyPolicy}
                        </a>
                      </>
                    )}
                  </label>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setShowPromoForm(false);
                      setEmail('');
                      setAgreedToPrivacy(false);
                    }}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                  >
                    {t.cancel}
                  </button>
                  <button
                    onClick={() => handlePromoRequest()}
                    disabled={isRequesting}
                    className="flex-1 px-3 py-2 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 disabled:opacity-50"
                  >
                    {isRequesting ? (t.requesting || 'Requesting...') : (ad.button_text || t.requestCode)}
                  </button>
                </div>
              </div>
            ) : (
              <button 
                onClick={() => setShowPromoForm(true)}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2.5 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center justify-center font-medium text-sm"
              >
                <Mail size={16} className="mr-2" />
                {ad.button_text || t.getPromoCode}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Promo delivery notice modal */}
      {promoDeliveryNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div
            className={`max-w-md w-full rounded-2xl shadow-2xl p-6 text-gray-800 relative ${
              promoDeliveryNotice.type === 'success' ? 'bg-green-50' :
              promoDeliveryNotice.type === 'error' ? 'bg-red-50' :
              'bg-blue-50'
            }`}
          >
            <button
              onClick={() => {
                setPromoDeliveryNotice(null);
                if (promoDeliveryNotice.type !== 'error') {
                  setShowPromoForm(false);
                }
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition"
            >
              ✕
            </button>
            {promoDeliveryNotice.title && (
              <h3 className="text-xl font-bold mb-2">{promoDeliveryNotice.title}</h3>
            )}
            <p className="mb-4">{promoDeliveryNotice.message}</p>
            <button
              onClick={() => {
                setPromoDeliveryNotice(null);
                if (promoDeliveryNotice.type !== 'error') {
                  setShowPromoForm(false);
                }
              }}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 font-medium"
            >
              {t.close || 'Close'}
            </button>
          </div>
        </div>
      )}

      {/* No Promo Confirmation Modal */}
      {showNoPromoConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t.directContactTitle || 'Direct contact confirmation'}
            </h3>
            <p className="text-gray-700 mb-4">
              {t.directContactNotification || 'You will be contacted by the advertisement provider for further details.'}
            </p>
            <p className="text-gray-600 mb-6">
              {t.directContactConfirmQuestion || 'Do you want to proceed?'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancelNoPromo}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleConfirmNoPromo}
                className="flex-1 px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                {t.proceed || 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redirect Confirmation Modal */}
      {showRedirectConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t.redirectConfirmTitle || 'Redirect confirmation'}
            </h3>
            <p className="text-gray-700 mb-4">
              {t.redirectConfirmMessage || "You will be redirected to the advertisement provider's website."}
            </p>
            <p className="text-gray-600 mb-6">
              {t.redirectConfirmQuestion || 'Do you want to proceed?'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancelRedirect}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleConfirmRedirect}
                className="flex-1 px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                {t.proceed || 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promocode Modal */}
      {showPromocodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in">
            <button
              onClick={closePromocodeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              ✕
            </button>

            {generatedPromocode === 'API_PENDING' ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {t.apiRequestSentTitle}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t.apiRequestSentDescription}
                </p>
                <button
                  onClick={closePromocodeModal}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-6 rounded-lg hover:from-orange-600 hover:to-red-700 transition duration-200 font-medium"
                >
                  {t.close || 'Close'}
                </button>
              </div>
            ) : isDirectContactMessage ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail size={32} className="text-blue-600" />
                </div>
                <p className="text-gray-700 text-base leading-relaxed">
                  {generatedPromocode}
                </p>
                <button
                  onClick={closePromocodeModal}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-6 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition duration-200 font-medium"
                >
                  {t.close || 'Done'}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-check-circle text-green-600"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">🎉 {t.yourPromocode || 'Your Promo Code!'}</h3>
                <p className="text-gray-600 mb-6">
                  {t.promocodeDescription || 'Here is your exclusive promo code. Copy it and use it at checkout!'}
                </p>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                  <div className="text-sm text-gray-600 mb-2">{t.yourPromocodeLabel || 'Your Promo Code:'}</div>
                  <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4 font-mono tracking-wider">
                    {generatedPromocode}
                  </div>
                  <button
                    onClick={() => copyToClipboard(generatedPromocode ?? '')}
                    className="flex items-center justify-center space-x-2 mx-auto px-4 py-2 rounded-lg transition duration-200 bg-blue-100 text-blue-700 hover:bg-blue-200"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-copy"
                    >
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                    </svg>
                    <span>{copiedToClipboard ? (t.copied || 'Copied!') : (t.copyCode || 'Copy Code')}</span>
                  </button>
                </div>
                
                {/* Target URL Button */}
                {promoTargetUrl && (
                  <div className="mb-6">
                    <a
                      href={promoTargetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4 rounded-lg hover:from-green-600 hover:to-emerald-700 transition duration-200 flex items-center justify-center font-medium"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                      {t.useYourCode || 'Use Your Code →'}
                    </a>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      {t.visitPartnerPage || "Visit the partner's page to redeem your code"}
                    </p>
                  </div>
                )}
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    {promoEmailReminder
                      ? promoEmailReminder
                      : <><strong>{t.promocodeTipTitle}</strong> {t.promocodeTipDescription}</>}
                  </p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={closePromocodeModal}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center justify-center font-medium"
                  >
                    {t.done || 'Done'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
